import { useEffect, useState } from "react";
import { Gift, Trophy, Star, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import MobileLayout from "@/components/MobileLayout";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

export default function ClubPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({});
  const [promos, setPromos] = useState([]);
  const [raffles, setRaffles] = useState([]);

  const level = user?.effective_client_level || user?.client_level || "Bronce";
  const orders = user?.lifetime_orders || 0;
  const pages = user?.lifetime_pages || 0;

  const load = async () => {
    try {
      const [settingsResp, promosResp, rafflesResp] = await Promise.all([
        api.get("/settings"),
        api.get("/promotions/mine"),
        api.get("/raffles/mine"),
      ]);
      setSettings(settingsResp.data || {});
      setPromos(promosResp.data || []);
      setRaffles(rafflesResp.data || []);
    } catch {
      setSettings({});
      setPromos([]);
      setRaffles([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const hideRaffle = async (raffleId) => {
    if (!window.confirm("¿Ocultar este sorteo de Mi KAPEI?")) return;
    try {
      await api.post(`/raffles/${raffleId}/hide`);
      setRaffles((prev) => prev.filter((r) => r.id !== raffleId));
    } catch {
      // Si falla, no hacemos nada visible para no molestar al cliente
    }
  };

  const levels = settings.loyalty_levels || [];
  const currentIdx = levels.findIndex((l) => (l.label || l.id) === level);
  const nextLevel = currentIdx >= 0
    ? levels.slice(currentIdx + 1).find((l) => !l.manual_only && l.mode !== "MANUAL")
    : null;

  let progressPct = 100;
  let missingText = "Has alcanzado el nivel automático más alto disponible.";

  if (nextLevel) {
    const needOrders = Math.max(0, (nextLevel.min_orders || 0) - orders);
    const needPages = Math.max(0, (nextLevel.min_pages || 0) - pages);
    const mode = (nextLevel.mode || "OR").toUpperCase();

    const orderPct = nextLevel.min_orders ? Math.min(100, Math.round((orders / nextLevel.min_orders) * 100)) : 100;
    const pagePct = nextLevel.min_pages ? Math.min(100, Math.round((pages / nextLevel.min_pages) * 100)) : 100;

    progressPct = mode === "AND" ? Math.min(orderPct, pagePct) : Math.max(orderPct, pagePct);
    missingText = mode === "AND"
      ? `Te faltan ${needOrders} pedidos y ${needPages} páginas.`
      : `Te faltan ${needOrders} pedidos o ${needPages} páginas.`;
  }

  if (level === "Histórico") {
    progressPct = 100;
    missingText = "Eres cliente Histórico KAPEI. Gracias por formar parte de nuestra historia.";
  }

  return (
    <MobileLayout>
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#4d7c0f]/10 px-4 py-2 text-sm font-black text-[#4d7c0f]">
          <Gift className="h-4 w-4" />
          Club KAPEI
        </div>
        <h1 className="mt-3 text-4xl font-black tracking-tight text-slate-900">
          Mi KAPEI
        </h1>
        <p className="mt-2 text-base text-slate-600">
          Tu espacio de niveles, promociones, sorteos y ventajas KAPEI.
        </p>
      </div>

      <section className="rounded-3xl border-2 border-[#d8b4e2] bg-gradient-to-br from-white to-[#faf5ff] p-5 shadow-sm">
        <div className="text-xs font-black uppercase tracking-widest text-[#7c3aed]">
          🎁 CLUB KAPEI
        </div>

        <div className="mt-3 flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-bold text-slate-500">Nivel actual</div>
            <div className="text-3xl font-black text-slate-900">{level}</div>
          </div>

          <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-[#4d7c0f] text-3xl text-white">
            {level === "Histórico" ? "🏛️" :
             level === "Diamante" ? "💎" :
             level === "Platino" ? "🔷" :
             level === "Oro" ? "🟡" :
             level === "Plata" ? "⚪" : "🟤"}
          </div>
        </div>

        <p className="mt-4 text-sm font-semibold text-slate-700">
          Cuanto más uses KAPEI, más oportunidades tendrás de acceder a promociones, sorteos y regalos exclusivos.
        </p>

        <div className="mt-5 h-3 overflow-hidden rounded-full bg-slate-200">
          <div
            className="h-full rounded-full bg-[#4d7c0f]"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        <div className="mt-4 rounded-2xl bg-white p-4">
          <div className="text-xs font-black uppercase text-slate-500">
            {nextLevel ? "Próximo nivel" : "Estado"}
          </div>
          <div className="mt-1 text-xl font-black text-slate-900">
            {nextLevel ? nextLevel.label : level}
          </div>
          <div className="mt-1 text-sm font-semibold text-slate-600">
            {missingText}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-2xl bg-white p-4">
            <div className="text-xs font-black uppercase text-slate-500">Pedidos terminados</div>
            <div className="mt-1 text-2xl font-black text-slate-900">{orders}</div>
          </div>
          <div className="rounded-2xl bg-white p-4">
            <div className="text-xs font-black uppercase text-slate-500">Páginas acumuladas</div>
            <div className="mt-1 text-2xl font-black text-slate-900">{pages}</div>
          </div>
        </div>
      </section>

      <section className="mt-5 rounded-3xl border-2 border-slate-200 bg-white p-5">
        <div className="flex items-center gap-3">
          <Gift className="h-6 w-6 text-[#4d7c0f]" />
          <div>
            <div className="font-black text-slate-900">Promociones disponibles para ti</div>
            <div className="text-sm text-slate-600">
              Aquí verás ofertas, regalos y ventajas activas para tu cuenta.
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {promos.map((promo) => (
            <div key={promo.code} className="rounded-2xl bg-[#faf5ff] p-4">
              <div className="flex items-start gap-3">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#4d7c0f] text-white">
                  {promo.claimed || promo.assigned ? <CheckCircle2 className="h-6 w-6" /> : <Gift className="h-6 w-6" />}
                </div>
                <div className="flex-1">
                  <div className="font-black text-slate-900">{promo.title || promo.code}</div>
                  {promo.description && (
                    <div className="mt-1 text-sm text-slate-600">{promo.description}</div>
                  )}
                  {promo.assigned ? (
                    <div className="mt-2 rounded-xl bg-amber-100 p-2 text-xs font-black text-amber-800">
                      Aplicada a tu próximo pedido.
                    </div>
                  ) : promo.claimed ? (
                    <div className="mt-2 rounded-xl bg-[#4d7c0f]/10 p-2 text-xs font-black text-[#4d7c0f]">
                      Preparada para tu próximo pedido.
                    </div>
                  ) : (
                    <div className="mt-2 text-xs font-bold text-slate-500">
                      Disponible para activar desde Inicio.
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {promos.length === 0 && (
            <div className="rounded-2xl bg-slate-50 p-4 text-sm font-semibold text-slate-600">
              Ahora mismo no tienes promociones activas. Vuelve a consultar esta sección más adelante.
            </div>
          )}
        </div>
      </section>

      <section className="mt-5 grid grid-cols-1 gap-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <Trophy className="h-6 w-6 text-[#4d7c0f]" />
            <div>
              <div className="font-black text-slate-900">Sorteos KAPEI</div>
              <div className="text-sm text-slate-600">
                Sorteos activos disponibles para tu nivel y cuenta.
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {raffles.map((raffle) => (
              <div key={raffle.id} className="rounded-2xl bg-[#faf5ff] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="font-black text-slate-900">
                    🏆 {raffle.title}
                  </div>
                  {raffle.status === "awarded" && (
                    <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-black text-amber-800">
                      Sorteado
                    </span>
                  )}
                </div>
                {raffle.description && (
                  <div className="mt-1 text-sm text-slate-600">
                    {raffle.description}
                  </div>
                )}
                {raffle.prize && (
                  <div className="mt-2 rounded-xl bg-white p-3 text-sm font-bold text-slate-700">
                    🎁 Premio: {raffle.prize}
                  </div>
                )}
                <div className="mt-2 text-xs font-semibold text-slate-500">
                  {raffle.ends_at ? `Participación hasta: ${raffle.ends_at}` : "Sin fecha final visible"}
                  {raffle.claim_until ? ` · Premio reclamable hasta: ${raffle.claim_until}` : ""}
                </div>

                {raffle.user_entry_count > 0 && (
                  <div className="mt-3 rounded-xl bg-white p-3 text-sm">
                    <div className="font-black text-slate-900">
                      Tus participaciones: {raffle.user_entry_count}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {(raffle.user_order_codes || []).map((code) => (
                        <span key={code} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-700">
                          {code}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {raffle.winner_order_codes?.length > 0 && (
                  <div className={`mt-3 rounded-xl p-3 text-sm font-bold ${
                    raffle.is_winner ? "bg-amber-100 text-amber-900" : "bg-slate-100 text-slate-700"
                  }`}>
                    {raffle.is_winner ? (
                      <>
                        <div>🎉 ¡Has ganado este sorteo!</div>
                        <div className="mt-2 text-xs font-black uppercase tracking-wide">
                          {((raffle.user_order_codes || []).filter((code) => (raffle.winner_order_codes || []).includes(code))).length > 1
                            ? "Tus pedidos ganadores"
                            : "Tu pedido ganador"}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {((raffle.user_order_codes || []).filter((code) => (raffle.winner_order_codes || []).includes(code))).map((code) => (
                            <span key={code} className="rounded-full bg-white px-3 py-1 text-xs font-black text-amber-900">
                              {code}
                            </span>
                          ))}
                        </div>
                        {raffle.prize && (
                          <div className="mt-2">
                            Premio: {raffle.prize}
                          </div>
                        )}
                        <div className="mt-2 text-xs font-semibold">
                          Puedes activar este premio cuando quieras usarlo. Debe canjearse completo en un único pedido.
                        </div>
                      </>
                    ) : (
                      <>
                        <div>Resultado del sorteo</div>
                        <div className="mt-1 font-black">
                          Ganadores: {raffle.winner_order_codes.join(", ")}
                        </div>
                      </>
                    )}
                  </div>
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                  {raffle.active && (
                    <Link
                      to="/send?mode=quick"
                      className="inline-flex rounded-full bg-[#4d7c0f] px-4 py-2 text-xs font-black text-white shadow hover:bg-[#3f6212]"
                    >
                      Hacer pedido y participar
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => hideRaffle(raffle.id)}
                    className="inline-flex rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-500"
                  >
                    Ocultar
                  </button>
                </div>
              </div>
            ))}

            {raffles.length === 0 && (
              <div className="rounded-2xl bg-slate-50 p-4 text-sm font-semibold text-slate-600">
                Ahora mismo no tienes sorteos activos disponibles. Vuelve a consultar esta sección más adelante.
              </div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <Star className="h-6 w-6 text-[#4d7c0f]" />
            <div>
              <div className="font-black text-slate-900">Ventajas del Club</div>
              <div className="text-sm text-slate-600">Promociones y beneficios especiales según tu nivel.</div>
            </div>
          </div>
        </div>
      </section>
    </MobileLayout>
  );
}
